import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import Logo from '../../components/Logo'
import { Eye, EyeOff, AlertCircle, CheckCircle, Loader } from 'lucide-react'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [sessionReady, setSessionReady] = useState(false)

  // Handle Supabase recovery tokens from URL
  useEffect(() => {
    const handleRecoveryToken = async () => {
      try {
        // Check for tokens in URL hash (Supabase puts them there)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token')
        const type = hashParams.get('type') || searchParams.get('type')

        // If this is a recovery flow with tokens
        if (accessToken && type === 'recovery') {
          // Set the session using the tokens
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || ''
          })

          if (error) {
            console.error('Session error:', error)
            setError('Invalid or expired reset link. Please request a new password reset.')
            setInitializing(false)
            return
          }

          if (data.session) {
            // Clear the URL hash/params for cleaner UX
            window.history.replaceState(null, '', window.location.pathname)
            setSessionReady(true)
          }
        } else {
          // Check if user already has an active session (from previous token handling)
          const { data: { session } } = await supabase.auth.getSession()
          if (session) {
            setSessionReady(true)
          } else {
            setError('No valid reset session found. Please request a new password reset link.')
          }
        }
      } catch (err) {
        console.error('Recovery token error:', err)
        setError('An error occurred. Please try requesting a new password reset.')
      } finally {
        setInitializing(false)
      }
    }

    handleRecoveryToken()
  }, [searchParams])

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters'
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match'
    }
    return null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const passwordError = validatePassword()
    if (passwordError) {
      setError(passwordError)
      return
    }

    setLoading(true)

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) {
        if (error.message.includes('should be different')) {
          setError('New password must be different from your current password')
        } else {
          setError(error.message)
        }
        return
      }

      setSuccess(true)

      // Sign out to clear the recovery session, then redirect to login
      await supabase.auth.signOut()

      setTimeout(() => {
        navigate('/login')
      }, 3000)
    } catch (err) {
      console.error('Password update error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Show loading while checking for recovery token
  if (initializing) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
        <div className="text-center">
          <Loader size={32} className="animate-spin text-rdc-primary mx-auto mb-4" />
          <p className="text-rdc-taupe">Verifying reset link...</p>
        </div>
      </div>
    )
  }

  // Show error if no valid session
  if (!sessionReady && error) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream max-w-md w-full text-center">
          <Logo className="mb-6" />
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="font-display text-xl font-semibold text-black mb-2">
            Invalid Reset Link
          </h1>
          <p className="text-rdc-taupe mb-6">
            {error}
          </p>
          <Link to="/forgot-password" className="btn-primary inline-block">
            Request New Reset Link
          </Link>
        </div>
      </div>
    )
  }

  // Show success message
  if (success) {
    return (
      <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream max-w-md w-full text-center">
          <Logo className="mb-6" />
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h1 className="font-display text-xl font-semibold text-black mb-2">
            Password Updated
          </h1>
          <p className="text-rdc-taupe mb-6">
            Your password has been successfully reset.
            You'll be redirected to the login page shortly.
          </p>
          <Link to="/login" className="btn-primary inline-block">
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  // Show password reset form
  return (
    <div className="min-h-screen bg-rdc-cream flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm p-8 border border-rdc-cream">
          <Logo className="mb-6" />

          <h1 className="font-display text-2xl font-semibold text-black mb-2">
            Set new password
          </h1>
          <p className="text-rdc-taupe mb-8">
            Enter your new password below.
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pr-12"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-rdc-taupe hover:text-rdc-dark-gray"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-rdc-taupe">
                Must be at least 8 characters
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-rdc-dark-gray mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field"
                placeholder="••••••••"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Updating...
                </span>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
